import { memo, useCallback, useState } from 'react';
import Button from '@wg/wows-react-uikit/components/Button/Button';
import ButtonGroup from '@wg/wows-react-uikit/components/ButtonGroup/ButtonGroup';
import Content from '@wg/wows-react-uikit/components/Content/Content';
import Currency from '@wg/wows-react-uikit/components/Currency/Currency';
import DialogBody from '@wg/wows-react-uikit/components/Dialog/DialogBody';
import DialogContainer from '@wg/wows-react-uikit/components/Dialog/DialogContainer';
import DialogFooter from '@wg/wows-react-uikit/components/Dialog/DialogFooter';
import DialogHeader from '@wg/wows-react-uikit/components/Dialog/DialogHeader';
import Interpolate from '@wg/wows-react-uikit/components/Interpolate/Interpolate';

import settings from '~/settings';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { t } from '~/helpers/localization';
import { useAppDispatch } from '~/store';
import { increaseBuildingLevelThunk } from '~/store/thunks/supplyThunks';
import { playClanBaseBuilding } from '~/web2ClientAPI/sounds';

import styles from './BuildTreasuryDialog.module.scss';
import img from './images/treasury.png';
import img2x from './images/treasury@2x.png';

import type { BUILDINGS } from '~/Actions/ActionSupply';
import type { IDialog } from '~/types/declaration';

const TREASURY = 'treasury' satisfies BUILDINGS;

type BuildTreasuryDialogProps = IDialog;

const BuildTreasuryDialog = ({ hideDialog }: BuildTreasuryDialogProps) => {
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const treasurySettings = settings.supply.buildings.find((b) => b.name === TREASURY);
  const treasuryBuildCost = treasurySettings?.levels[1]?.meta?.cost || 0;

  const buildTreasury = useCallback(async () => {
    setIsLoading(true);
    playClanBaseBuilding();

    try {
      await dispatch(increaseBuildingLevelThunk(TREASURY));
      hideDialog();
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, hideDialog]);

  const buildTreasuryClickHandler = useCallback(() => {
    dwhExport.push(DWH_EVENTS.TREASURY.BUILD_MODAL_CONFIRM_CLICK);

    void buildTreasury();
  }, [buildTreasury]);

  const cancelClickHandler = useCallback(() => {
    dwhExport.push(DWH_EVENTS.TREASURY.BUILD_MODAL_CANCEL_CLICK);

    hideDialog();
  }, [hideDialog]);

  return (
    <div className={styles.dialogWrapper}>
      <DialogContainer>
        <DialogHeader>{t('Confirmation')}</DialogHeader>
        <DialogBody adaptiveHeight>
          <Content className={styles.content}>
            <img
              className={styles.img}
              src={img}
              srcSet={`${img} 1x, ${img2x} 2x`}
              alt={t('Treasury')}
              width={540}
              height={286}
            />
          </Content>
        </DialogBody>
        <DialogFooter>
          <div className={styles.footer}>
            <div className={styles.footerText}>
              {treasuryBuildCost === 0 ? (
                t('Build Treasury for free?')
              ) : (
                <Interpolate
                  className={styles.buildingConfirmQuestion}
                  str={t('Улучшить за %(cost)s?')}
                  cost={<Currency isFlat isBold type="oil" amount={treasuryBuildCost} />}
                />
              )}
            </div>
            <ButtonGroup isLoading={isLoading}>
              <Button isFlat onClick={buildTreasuryClickHandler}>
                {t('Да')}
              </Button>
              <Button isFlat onClick={cancelClickHandler}>
                {t('Закрыть')}
              </Button>
            </ButtonGroup>
          </div>
        </DialogFooter>
      </DialogContainer>
    </div>
  );
};

export default memo(BuildTreasuryDialog);
